import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, Save, X } from "lucide-react";

const STATUS_OPTIONS = ["Abierto", "Iniciado", "En espera", "Cerrado"];

const STATUS_STYLE = {
  "Abierto":   { bg: "rgba(74,144,226,0.15)", color: "#88B8F0" },
  "Iniciado":  { bg: "rgba(245,184,46,0.18)", color: "#F5B82E" },
  "En espera": { bg: "rgba(148,164,192,0.18)", color: "#94A4C0" },
  "Cerrado":   { bg: "rgba(46,186,126,0.18)", color: "#6FE3B2" },
};

const EMPTY = {
  activity: "",
  comments: "",
  status: "Abierto",
  requested_at: new Date().toISOString().slice(0, 10),
  due_date: "",
};

export default function TrackingPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/data/tracking_items");
      // Sort by requested_at desc
      data.sort((a, b) => (b.requested_at || "").localeCompare(a.requested_at || ""));
      setItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!draft.activity.trim()) { alert("La actividad es obligatoria."); return; }
    try {
      await api.post("/data/tracking_items", draft);
      setDraft(EMPTY); setAdding(false); load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  }

  async function saveEdit() {
    try {
      await api.patch(`/data/tracking_items/${editingId}`, editDraft);
      setEditingId(null); load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  }

  async function del(id) {
    if (!window.confirm("¿Eliminar esta actividad?")) return;
    try {
      await api.delete(`/data/tracking_items/${id}`);
      load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  }

  return (
    <div className="card mt-4" data-testid="tracking-panel" style={{ background: "rgba(245,184,46,0.04)", border: "1px solid rgba(245,184,46,0.3)" }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="card-title" style={{ marginBottom: 4 }}>Panel de Seguimiento</div>
          <div className="text-xs text-dim">Actividades solicitadas por Gerencia. Solo se gestionan desde este panel.</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(!adding)} data-testid="tracking-toggle-add">
          {adding ? <><X size={14} /> Cancelar</> : <><Plus size={14} /> Nueva actividad</>}
        </button>
      </div>

      {adding && (
        <div className="card mb-3" data-testid="tracking-new-form" style={{ background: "var(--bg-2)" }}>
          <div className="grid gap-2" style={{ gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr auto", alignItems: "end" }}>
            <div>
              <label className="label">Actividad *</label>
              <input className="input" value={draft.activity} onChange={(e) => setDraft({ ...draft, activity: e.target.value })} data-testid="tracking-new-activity" />
            </div>
            <div>
              <label className="label">Comentarios</label>
              <input className="input" value={draft.comments} onChange={(e) => setDraft({ ...draft, comments: e.target.value })} data-testid="tracking-new-comments" />
            </div>
            <div>
              <label className="label">Estatus</label>
              <select className="select" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="tracking-new-status">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Fecha solicitud</label>
              <input className="input" type="date" value={draft.requested_at} onChange={(e) => setDraft({ ...draft, requested_at: e.target.value })} data-testid="tracking-new-requested" />
            </div>
            <div>
              <label className="label">Fecha cierre</label>
              <input className="input" type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} data-testid="tracking-new-due" />
            </div>
            <div>
              <button className="btn btn-primary" onClick={create} data-testid="tracking-new-save"><Save size={14} /></button>
            </div>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="table" data-testid="tracking-table">
          <thead>
            <tr>
              <th>Actividad</th>
              <th>Comentarios</th>
              <th style={{ width: 130 }}>Estatus</th>
              <th style={{ width: 130 }}>Solicitud</th>
              <th style={{ width: 130 }}>Cierre estimado</th>
              <th style={{ width: 100, textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-dim">Cargando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="text-dim" style={{ textAlign: "center", padding: 24 }}>Sin actividades de gerencia registradas.</td></tr>
            )}
            {items.map((it) => {
              const isEditing = editingId === it.id;
              if (isEditing) {
                return (
                  <tr key={it.id} data-testid={`tracking-edit-${it.id}`}>
                    <td><input className="input" value={editDraft.activity} onChange={(e) => setEditDraft({ ...editDraft, activity: e.target.value })} /></td>
                    <td><input className="input" value={editDraft.comments || ""} onChange={(e) => setEditDraft({ ...editDraft, comments: e.target.value })} /></td>
                    <td>
                      <select className="select" value={editDraft.status} onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td><input className="input" type="date" value={editDraft.requested_at || ""} onChange={(e) => setEditDraft({ ...editDraft, requested_at: e.target.value })} /></td>
                    <td><input className="input" type="date" value={editDraft.due_date || ""} onChange={(e) => setEditDraft({ ...editDraft, due_date: e.target.value })} /></td>
                    <td style={{ textAlign: "right" }}>
                      <div className="flex gap-1 justify-end">
                        <button className="btn btn-sm btn-primary" onClick={saveEdit} data-testid={`tracking-save-${it.id}`}><Save size={12} /></button>
                        <button className="btn btn-sm" onClick={() => setEditingId(null)}><X size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              }
              const sStyle = STATUS_STYLE[it.status] || STATUS_STYLE["Abierto"];
              return (
                <tr key={it.id} data-testid={`tracking-row-${it.id}`}>
                  <td><b>{it.activity}</b></td>
                  <td className="text-xs">{it.comments || "—"}</td>
                  <td>
                    <span className="badge" style={{ background: sStyle.bg, color: sStyle.color, fontWeight: 600 }}>
                      {it.status || "Abierto"}
                    </span>
                  </td>
                  <td className="text-xs">{it.requested_at || "—"}</td>
                  <td className="text-xs">{it.due_date || "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <div className="flex gap-1 justify-end">
                      <button className="btn btn-sm" onClick={() => { setEditingId(it.id); setEditDraft({ ...it }); }} data-testid={`tracking-edit-btn-${it.id}`}><Pencil size={12} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(it.id)} data-testid={`tracking-delete-${it.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
